import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import { Button } from 'components/core/Button';
import { Expand } from 'components/core/Expand';
import { H4 } from 'components/core/Headline';
import { Paragraph } from 'components/core/Paragraph';
import { Text } from 'components/core/Text';
import { Group, Page } from 'components/core/Page';
import { ImageCarousel } from 'components/core/ImageCarousel';
import { BackToTop } from 'components/core/BackToTop';
import { NearbyUnits } from 'components/search/NearbyUnits';
import { SearchBox } from 'components/search/SearchBox';
import { FacilityRow } from 'components/search/FacilityRow';
import { FacilityDetails } from 'components/facility/FacilityDetails';
import { FacilityImageNotAvailable } from 'components/facility/ImageNotAvailable';
import { FacilityBreadcrumbs } from 'components/facility/FacilityBreadcrumbs';
import { FacilityReviews } from 'components/facility/FacilityReviews';
import { InactiveFacilityBanner } from 'components/facility/InactiveFacilityBanner';
import { CallCenterCTA } from 'components/contact/CallCenterCTA';
import { Plus } from 'components/svg';

import './facilityPage.scss';
import './InactiveFacilityPage.scss';

export function InactiveFacilityPage({ images = [], actions = {}, ...restProps }) {
	const props = { images, actions, ...restProps };
	const nearbyPrices = get(
		props,
		'facility.nearby.pricingStats.lowestPrices'
	);

	const facilityZipCode = get(props, 'facility.location.zip');

	return (
		<Page
			className="facility-page in-network-facility-page inactive-facility-page"
			pageType={props.pageType}
		>
			<div>
				<div className="content-container">
					<FacilityBreadcrumbs
						facility={props.facility}
					/>
				</div>
				<div className="main-content">
					<div className="content-container">
						<InactiveFacilityBanner />
					</div>
					<div className="row content-container inactive-facility-info">
						<div className="split-column column-left">
							{props.facility && (
								<div>
									<FacilityDetails
										facilityName={props.facility.name}
										facilityId={props.facility.id}
										facilityZipCode={facilityZipCode}
										locationText={
											props.facility.location.text
										}
										reviewAverage={get(
											props.facility,
											'reviewMetadata.average'
										)}
										reviewCount={get(
											props.facility,
											'reviewMetadata.count'
										)}
										onGetHoursAndDirections={
											props.actions
												.getFacilityHoursAndDirection
										}
										getLocalPhoneNumberByZipCode={
											props.actions
												.getLocalPhoneNumberByZipCode
										}
										getLocalPhoneNumberByCityState={
											props.actions
												.getLocalPhoneNumberByCityState
										}
									/>
									<div className="facility-description-container">
										<H4
											strong
											size="medium"
											className="section-heading"
										>
											About {props.facility.name}
										</H4>
										<Expand
											showing={props.descriptionExpanded}
											showTeaser
										>
											<div className="facility-description">
												<Paragraph
													size="small"
													light
												>
													<span
														// eslint-disable-next-line react/no-danger
														dangerouslySetInnerHTML={{
															__html: decodeURIComponent(
																props.facility
																	.description
															).replace(
																/\n/gi,
																'<br />'
															),
														}}
													/>
												</Paragraph>
											</div>
										</Expand>
										<Button
											link
											preContent={<Plus />}
											onClick={
												props.toggleDescriptionExpanded
											}
											className="show-more-button"
										>
											<Text size="small">
												Read{' '}
												{props.descriptionExpanded
													? 'less'
													: 'more'}{' '}
												description
											</Text>
										</Button>
									</div>
								</div>
							)}
						</div>
						<div className="split-column column-right">
							<div className="image-and-alerts">
								<div className="facility-image-container">
									{!!props.images.length && (
										<ImageCarousel
											className="facility-image-carousel"
											images={props.images}
										/>
									)}
									{!props.images.length && (
										<FacilityImageNotAvailable size="large" />
									)}
								</div>
							</div>
						</div>
					</div>
					<div className="divider" />
					<Fragment>
						<div className="row content-container nearby-facilities-row">
							<div className="column">
								{get(props, 'nearbyFacilities.length', 0) >
								0 ? (
									<>
										<FacilityRow
											className="nearby-facilities-page"
											location={props.facility.location}
											facilities={props.nearbyFacilities}
											headerSize="medium"
										/>
									</>
								) : (
									<Text>No available nearby facilities</Text>
								)}
							</div>
						</div>
						{!!nearbyPrices && (
							<div className="row content-container nearby-units-row">
								<Group className="column">
									<H4
										strong
										size="small"
										className="section-heading small-flush-padding"
									>
										Units available at nearby facilities
									</H4>
									<NearbyUnits
										nearbyZip={facilityZipCode}
										nearbyPrices={nearbyPrices}
									/>
								</Group>
							</div>
						)}
					</Fragment>
				</div>
				<div className="content-container call-or-search">
					<CallCenterCTA
						phone={props.phone}
						getTollfreePhoneNumber={
							props.actions.getTollfreePhoneNumber
						}
					/>
					<SearchBox
						inputLabel="Enter ZIP code"
						handleSearch={props.handleSearch}
					/>
				</div>
			</div>
			<div
				className="reviews-content-wrapper"
				id="reviews"
			>
				<div className="content-container">
					<FacilityReviews
						reviews={props.facility.reviews}
						reviewAverage={get(
							props.facility,
							'reviewMetadata.average',
							0
						)}
						reviewCount={get(
							props.facility,
							'reviewMetadata.count',
							0
						)}
						ratingCounts={get(
							props.facility,
							'reviewMetadata.ratingCounts'
						)}
						pros={get(props.facility, 'reviewMetadata.pros')}
						cons={get(props.facility, 'reviewMetadata.cons')}
						recommendedCount={get(
							props.facility,
							'reviewMetadata.recommendedCount'
						)}
						recommendedRate={get(
							props.facility,
							'reviewMetadata.recommendedRate'
						)}
						facilityId={props.facility.id}
						facilityUrl={props.facility.url.facility}
						fullFacilityUrl={props.fullFacilityUrl}
						currentReviewPage={props.reviewPage}
						images={get(props, 'images', []).map(
							(image) => image.src
						)}
						facilityName={get(props, 'facility.name')}
					/>
				</div>
			</div>
			<BackToTop />
			<script
				key="schema"
				type="application/ld+json"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: props.pageSchema }}
			/>
		</Page>
	);
}

InactiveFacilityPage.propTypes = {
	actions: PropTypes.shape({
		createBooking: PropTypes.func,
		formSubmit: PropTypes.func,
		formChange: PropTypes.func,
		getFacilityHoursAndDirection: PropTypes.func,
		getLocalPhoneNumberByZipCode: PropTypes.func.isRequired,
		getLocalPhoneNumberByCityState: PropTypes.func.isRequired,
		getTollfreePhoneNumber: PropTypes.func.isRequired,
	}),
	facility: PropTypes.object,
	handleSearch: PropTypes.func,
	images: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			alt: PropTypes.string,
		})
	),
	phone: PropTypes.object,
	descriptionExpanded: PropTypes.bool,
	toggleDescriptionExpanded: PropTypes.func,
	reviewPage: PropTypes.number,
	fullFacilityUrl: PropTypes.string,
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	pageSchema: PropTypes.string,
	pageType: PropTypes.string,
};

export default InactiveFacilityPage;
